import { computed, effect, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import type { MultiPolygon as GeoJsonMultiPolygon } from 'geojson';
import { firstValueFrom } from 'rxjs';
// @ts-expect-error No type declaration type exists
import { parse as wktParse } from 'wkt';

import { MapFieldInterventionSource, MapLayerSource } from '@connect-services/client/components/map/map.component';
import { GeomLayer } from '@connect-services/client/models/geom-layer.model';
import {
    FieldIntervention,
    FieldInterventionEntity,
    FieldInterventionsService,
    Layer,
    LayerWithAttributes,
    MultiPolygon,
    Polygon,
} from '@connect-services/client/sdk/api';
import { AbstractFieldInterventionListService } from '@connect-services/client/services/abstract-field-intervention-list.service';
import { AssertService } from '@connect-services/client/services/assert.service';
import { FieldInterventionService } from '@connect-services/client/services/field-intervention.service';
import { FieldInterventionEntitiesFilterService } from '@connect-services/client/services/field-intervention-entities-filter.service';
import { GeographicFilterService } from '@connect-services/client/services/geographic-filter.service';
import { LayerService } from '@connect-services/client/services/layer.service';
import { MapMaskService, ZONE_DELIMITER_MASK_ID } from '@connect-services/client/services/map/mask/map-mask.service';
import { MapFieldInterventionService } from '@connect-services/client/services/map-field-intervention.service';
import { MapGeoJSONFeatureWithLayer } from '@connect-services/client/services/map-popup.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { TableService } from '@connect-services/client/services/table.service';
import { SortState } from '@connect-services/src/lib/components/table/table.component';
import {
    defaultPaginationParameters,
    OrderEnum,
    paginationMaxTake,
    PaginationParameters,
} from '@shared/client/models/pagination.model';

export enum EntitiesTableTypeEnum {
    PLANNING = 'PLANNING',
    FILTERED_TABLE = 'FILTERED_TABLE',
}

export const defaultFieldInterventionLayerListPaginationParameters: Readonly<PaginationParameters> = {
    order: OrderEnum.ASC,
    orderBy: 'name',
    page: 0,
    take: 50,
};

@Injectable({
    providedIn: 'root',
})
export class EntitiesTableByLayerService extends AbstractFieldInterventionListService<
    FieldInterventionEntity,
    LayerWithAttributes
> {
    public readonly areEntitiesPlanned: WritableSignal<boolean> = signal(false);
    public readonly areLayersDefined = computed(() => !!(this.layerNames().length && this.layersByName()));
    public columnsToSearchValueIn: string[] = ['dataAttributes.businessIdentifier'];
    public readonly isPlanningSelectionActive = signal(false);
    public readonly isSourceDrawn = signal(false);
    public readonly layerNames: WritableSignal<string[]> = signal([]);
    public readonly layers: WritableSignal<LayerWithAttributes[]> = signal([]);
    public readonly layersByName: WritableSignal<Record<string, LayerWithAttributes[]> | null> = signal(null);
    public readonly selectedLayerNumber = computed(() => {
        if (!this.layersByName()) {
            return 0;
        }

        return Object.values(this.layersByName()!).reduce((accumulator, groupedLayers) => {
            const selectedEntities = groupedLayers.flatMap((groupedLayer) => this.selectedEntities()[groupedLayer.id]);

            if (
                selectedEntities.length ||
                this.fullSelectedLayers().some((layerId) =>
                    groupedLayers.map((groupedLayer) => groupedLayer.id).includes(layerId),
                )
            ) {
                return accumulator + 1;
            }

            return accumulator;
        }, 0);
    });
    public readonly tableType: WritableSignal<EntitiesTableTypeEnum | null> = signal(null);
    public readonly totalItemsCurrentLayer: Signal<number> = computed(() => {
        let totalItems = 0;
        for (const activeLayer of this.activeLayers()) {
            totalItems += activeLayer?.entitiesCount ?? 0;
        }

        return totalItems;
    });

    private readonly fieldInterventionEntitiesFilterService = inject(FieldInterventionEntitiesFilterService);
    private readonly fieldInterventionService = inject(FieldInterventionService);
    private readonly geographicFilterService = inject(GeographicFilterService);
    private readonly layerService = inject(LayerService);
    private readonly mapFieldInterventionService = inject(MapFieldInterventionService);
    private readonly mapMaskService = inject(MapMaskService);
    private readonly sdkFieldInterventionService = inject(FieldInterventionsService);
    private readonly sourcesService = inject(SourcesService);
    private readonly tableService = inject(TableService);

    public constructor() {
        super();
        effect(() => {
            const isActive = this.isZoneDelimiterFilterActive();
            const wktString = this.zoneDelimiter();

            if (!isActive || !wktString) {
                this.mapMaskService.clearMasks([ZONE_DELIMITER_MASK_ID]);

                return;
            }

            try {
                const geoJson = wktParse(wktString);

                if (
                    geoJson &&
                    (geoJson.type === Polygon.TypeEnum.POLYGON || geoJson.type === MultiPolygon.TypeEnum.MULTI_POLYGON)
                ) {
                    const areaMultiPolygon: GeoJsonMultiPolygon =
                        geoJson.type === Polygon.TypeEnum.POLYGON
                            ? { coordinates: [geoJson.coordinates], type: MultiPolygon.TypeEnum.MULTI_POLYGON }
                            : { coordinates: geoJson.coordinates, type: MultiPolygon.TypeEnum.MULTI_POLYGON };

                    this.mapMaskService.applyMask(
                        {
                            area: areaMultiPolygon,
                            id: ZONE_DELIMITER_MASK_ID,
                            showMask: false,
                        },
                        {
                            hideOuterEntities: true,
                        },
                    );
                }
            } catch {}
        });
    }

    public override async fetchAllEntities(
        excludedIds?: string[],
        fullSelectedLayers?: string[],
        status?: FieldInterventionEntity.StatusEnum,
    ): Promise<FieldInterventionEntity[]> {
        if (!this.fieldInterventionBloc.value() && !this.activeLayers().length) {
            return [];
        }

        let hasNextPage = false;
        const paginationParameters: PaginationParameters = {
            ...defaultPaginationParameters,
            orderBy: 'businessIdentifier',
            take: paginationMaxTake,
        };

        const fieldInterventionEntities: FieldInterventionEntity[] = [];

        this.isLoading.set(true);
        do {
            await this.fieldInterventionEntitiesListBloc.load({
                excludedIds: excludedIds,
                fieldIntervention: this.fieldInterventionBloc.value()!.id,
                ...paginationParameters,
                layers: fullSelectedLayers ?? this.activeLayers().map((activeLayer) => activeLayer.id),
                properties: this.searchValue().length ? this.columnsToSearchValueIn : undefined,
                statuses: status ? [status] : undefined,
                value: this.searchValue(),
                zoneDelimiter: this.zoneDelimiter(),
                ...this.fieldInterventionEntitiesFilterService.filterByParams(),
            });

            fieldInterventionEntities.push(...this.fieldInterventionEntitiesListBloc.value()!.items);
            hasNextPage = this.fieldInterventionEntitiesListBloc.value()!.hasNextPage;
            paginationParameters.page += 1;
        } while (hasNextPage);

        this.isLoading.set(false);

        return fieldInterventionEntities;
    }

    public override async fetchEntities(): Promise<void> {
        if (!this.fieldInterventionBloc.value()) {
            return;
        }

        this.checkActiveLayer();

        this.isLoading.set(true);

        await this.fieldInterventionEntitiesListBloc.load(
            {
                fieldIntervention: this.fieldInterventionBloc.value()!.id,
                ...this.paginationParameters(),
                layers: this.activeLayers().map((activeLayer) => activeLayer.id),
                properties: this.searchValue().length ? this.columnsToSearchValueIn : undefined,
                value: this.searchValue(),
                zoneDelimiter: this.zoneDelimiter(),
                ...this.fieldInterventionEntitiesFilterService.filterByParams(),
            },
            { refresh: true },
        );

        this.entities.set(this.fieldInterventionEntitiesListBloc.value()!.items);

        this.isLoading.set(false);

        this.setPaginationParameters();

        if (!this.isSourceDrawn() || !this.currentSources().length) {
            return;
        }

        if (!this.fieldInterventionEntitiesFilterService.filterByParams()) {
            this.mapFieldInterventionService.resetFilter();
        }

        await this.mapFieldInterventionService.filterFieldInterventionEntitiesOnMap(
            this.activeLayers().map((layer) => layer.id),
            this.currentSources(),
            this.fieldInterventionEntitiesFilterService.filterByParams(),
        );
    }

    public async handleSearchFilter(): Promise<void> {
        this.resetPagination();
        await this.fetchEntities();
    }

    public async initializeSelectOptions(): Promise<void> {
        const allLayer = await this.layerService.getAllLayers();
        const onlyTableLayer = allLayer.items.filter((layer) => layer.type === Layer.TypeEnum.TABLE);
        this.geographicFilterService.isGeographicFilterAllowedOnComponent.set(false);
        this.geographicFilterService.isVisible.set(true);
        this.layerService.setLayers(onlyTableLayer);
    }

    public loadFieldInterventionLayers(): Promise<void> {
        return this.fieldInterventionLayerListBloc.load({
            id: this.fieldInterventionBloc.value()!.id,
            ...defaultFieldInterventionLayerListPaginationParameters,
            layer: this.fieldInterventionEntitiesFilterService.filterByParams()?.layers?.[0],
        });
    }

    public async onSortChange(sort?: SortState): Promise<void> {
        if (!sort) {
            this.paginationParameters.set({
                ...this.paginationParameters(),
                orderBy: undefined,
            });

            await this.fetchEntities();

            return;
        }

        this.paginationParameters.set(this.tableService.onSortStateChange(sort, this.paginationParameters()));
        await this.fetchEntities();
    }

    public resetData(): void {
        this.activeLayers.set([]);
        this.fullSelectedLayers.set([]);
        this.searchValue.set('');
        this.zoneDelimiter.set(undefined);
        this.isZoneDelimiterFilterActive.set(false);
        this.isFieldInterventionSelectionActive.set(false);
        this.currentSources.set([]);
        this.selectedEntities.set({});
        this.unselectedEntities.set({});
        this.totalItemsByLayer.set({});
        this.isSourceDrawn.set(false);
        this.tableType.set(null);

        this.isPlanningSelectionActive.set(false);
        this.fieldInterventionEntitiesListBloc.reset();
    }

    public resetSearch(): void {
        this.searchValue.set('');
        this.resetPagination();
    }

    public async resetTableData(): Promise<void> {
        this.resetPagination();
        await this.initializeTableWithData();
    }

    public setActiveLayerByName(activeLayerName: string): void {
        if (!this.layersByName() && !this.layersByName()![activeLayerName].length) {
            return;
        }

        this.activeLayers.set(this.layersByName()![activeLayerName]);
        this.paginationParameters.set({
            ...this.paginationParameters(),
            page: defaultPaginationParameters.page,
        });
    }

    public async setCurrentLayerSources(): Promise<void> {
        const sources = await Promise.all(
            this.activeLayers().map(async (layer: LayerWithAttributes): Promise<MapLayerSource> => {
                const style = await this.sourcesService.getLayerStyle(layer.layerStyle);

                return new MapLayerSource(GeomLayer.fromLayer(layer), style);
            }),
        );

        this.sourcesService.sources.set(sources);
        this.currentSources.set(sources);
    }

    public async setCurrentSources(fieldIntervention: FieldIntervention, currentLayerIds: string[]): Promise<void> {
        const sources = await Promise.all(
            currentLayerIds.map(async (currentLayerId) => {
                const layerStyle = await this.sourcesService.getLayerStyle(fieldIntervention.layerStyle);
                const fieldInterventionSource = new MapFieldInterventionSource(
                    currentLayerId,
                    fieldIntervention,
                    layerStyle,
                    currentLayerId,
                );
                fieldInterventionSource.isVisible = true;

                return fieldInterventionSource;
            }),
        );
        this.sourcesService.sources.set(sources);
        this.currentSources.set(sources);
    }

    public async setCurrentSourcesAndEntityType(): Promise<void> {
        await this.setCurrentSources(
            this.fieldInterventionBloc.value()!,
            this.activeLayers().map((currentLayer) => currentLayer.id),
        );
        this.setEntityType();
    }

    public async setLayers(): Promise<void> {
        const allLayersWithAttributes = await this.fieldInterventionService.fetchAllLayersWithAttributes();

        this.layersByName.set(
            allLayersWithAttributes.reduce(
                (acc: Record<string, LayerWithAttributes[]>, layerWithAttributes: LayerWithAttributes) => {
                    if (!acc[layerWithAttributes.name]) {
                        acc[layerWithAttributes.name] = [];
                    }

                    acc[layerWithAttributes.name].push(layerWithAttributes);

                    return acc;
                },
                {},
            ),
        );

        this.layers.set(allLayersWithAttributes);
        const uniqueLayerName = Array.from(
            new Set(allLayersWithAttributes.map((layerWithAttributes) => layerWithAttributes.name)),
        );
        this.layerNames.set(uniqueLayerName);
    }

    public setTotalItemsByLayer(): void {
        for (const activeLayer of this.activeLayers()) {
            this.totalItemsByLayer.set({
                ...this.totalItemsByLayer(),
                //all grouped layers will have the same
                [activeLayer.id]: this.fieldInterventionEntitiesListBloc.value()!.totalItems,
            });
        }
    }

    public async verifyEntitiesPlanned(): Promise<void> {
        if (!this.fieldInterventionBloc.value()) {
            return;
        }

        this.areEntitiesPlanned.set(
            await firstValueFrom(
                this.sdkFieldInterventionService.fieldInterventionControllerVerifyAllFieldInterventionEntitiesArePlanned(
                    {
                        id: this.fieldInterventionBloc.value()!.id,
                    },
                ),
            ),
        );
    }

    protected override buildEntityFromMapFeature(
        feature: MapGeoJSONFeatureWithLayer,
    ): FieldInterventionEntity | undefined {
        if (!feature.geometry || feature.geometry.type === 'GeometryCollection') {
            return undefined;
        }

        const entityId = feature.properties['entityId'];
        AssertService.isString(entityId);

        const status = feature.properties['status'];
        AssertService.isEnum(status, FieldInterventionEntity.StatusEnum);

        return {
            ...this.buildFieldInterventionEntityDefaults(),
            dataAttributes: {},
            fieldIntervention: this.fieldInterventionBloc.value()?.id ?? '',
            formIds: this.parseMapFeatureIds(feature.properties['formIds']),
            geom: feature.geometry,
            id: entityId,
            layer: feature.source,
            peopleIds: this.parseMapFeatureIds(feature.properties['peopleIds']),
            status,
        };
    }

    protected override getEntityId(entity: FieldInterventionEntity): string {
        return entity.id;
    }

    protected override getLayerId(entity: FieldInterventionEntity): string {
        return entity.layer;
    }

    private buildFieldInterventionEntityDefaults(): Omit<
        FieldInterventionEntity,
        'dataAttributes' | 'fieldIntervention' | 'formIds' | 'geom' | 'id' | 'layer' | 'peopleIds' | 'status'
    > {
        return {
            createdAt: new Date(),
            dataAttributesModifiedAt: null,
            dataAttributesModifiedBy: null,
            depositVersion: null,
            forms: [],
            gpsPoints: null,
            hasAttentionPoint: false,
            locationModifiedAt: null,
            locationModifiedBy: null,
            notApplicableFormIds: null,
            refSid: null,
            updatedAt: new Date(),
        };
    }

    private checkActiveLayer(): void {
        if (
            this.activeLayers().length ||
            !this.fieldInterventionLayerListBloc.value()?.items.length ||
            !this.layersByName()
        ) {
            return;
        }

        const layerName = this.fieldInterventionLayerListBloc.value()!.items[0].name;
        this.activeLayers.set(this.layersByName()![layerName]);
    }

    private async initializeTableWithData(): Promise<void> {
        this.isLoading.set(true);

        await this.fetchEntities();
        await this.loadFieldInterventionLayers();

        this.isLoading.set(false);
    }

    private parseMapFeatureIds(value: unknown): string[] {
        if (Array.isArray(value)) {
            return value;
        }

        if (typeof value === 'string') {
            return JSON.parse(value) as string[];
        }

        return [];
    }

    private setPaginationParameters(): void {
        this.paginationParameters.set({
            ...this.paginationParameters(),
            take: this.fieldInterventionEntitiesListBloc.value()!.take,
            totalItems: this.fieldInterventionEntitiesListBloc.value()!.totalItems,
        });
    }
}
